import Animated, {
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import React, { useEffect } from 'react';
import { Circle, Svg } from 'react-native-svg';
import { createStyles } from '@/contexts/theme-context';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const DEFAULT_RING_SIZE = 40;
const DEFAULT_RING_STROKE_WIDTH = 4;
const RING_TIMING_MS = 250;

type TypographyVariant = React.ComponentProps<typeof Typography>['variant'];

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

export interface SegmentRingProps {
  acceptedCount: number;
  actionedCount: number;
  labelVariant?: TypographyVariant;
  overriddenCount: number;
  progressPercent: number;
  publishedCount: number;
  size?: number;
  strokeWidth?: number;
}

// Four concentric arcs animated independently. Larger arcs sit beneath smaller
// ones so the visible band of each color = its share of the total, and easing
// each strokeDashoffset gives the segments a smooth grow/shrink transition when
// actions change. Order around the ring: published (black) → accepted (green) →
// overridden (blue) → rejected (red).
export const SegmentRing: React.FC<SegmentRingProps> = ({
  acceptedCount,
  actionedCount,
  labelVariant = 'label-sm',
  overriddenCount,
  progressPercent,
  publishedCount,
  size = DEFAULT_RING_SIZE,
  strokeWidth = DEFAULT_RING_STROKE_WIDTH,
}) => {
  const [styles, theme] = useStyles();

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const publishedShare =
    actionedCount === 0 ? 0 : publishedCount / actionedCount;
  const acceptedShare = actionedCount === 0 ? 0 : acceptedCount / actionedCount;
  const overriddenShare =
    actionedCount === 0 ? 0 : overriddenCount / actionedCount;
  const publishedTarget = progressPercent * publishedShare;
  const acceptedTarget = progressPercent * (publishedShare + acceptedShare);
  const overriddenTarget =
    progressPercent * (publishedShare + acceptedShare + overriddenShare);

  const totalProgress = useSharedValue(0);
  const overriddenProgress = useSharedValue(0);
  const acceptedProgress = useSharedValue(0);
  const publishedProgress = useSharedValue(0);

  useEffect(() => {
    totalProgress.value = withTiming(progressPercent, {
      duration: RING_TIMING_MS,
    });
    overriddenProgress.value = withTiming(overriddenTarget, {
      duration: RING_TIMING_MS,
    });
    acceptedProgress.value = withTiming(acceptedTarget, {
      duration: RING_TIMING_MS,
    });
    publishedProgress.value = withTiming(publishedTarget, {
      duration: RING_TIMING_MS,
    });
  }, [
    acceptedProgress,
    acceptedTarget,
    overriddenProgress,
    overriddenTarget,
    progressPercent,
    publishedProgress,
    publishedTarget,
    totalProgress,
  ]);

  const totalArcProps = useAnimatedProps(() => ({
    strokeDashoffset:
      circumference -
      (Math.max(0, Math.min(100, totalProgress.value)) / 100) * circumference,
  }));
  const overriddenArcProps = useAnimatedProps(() => ({
    strokeDashoffset:
      circumference -
      (Math.max(0, Math.min(100, overriddenProgress.value)) / 100) *
        circumference,
  }));
  const acceptedArcProps = useAnimatedProps(() => ({
    strokeDashoffset:
      circumference -
      (Math.max(0, Math.min(100, acceptedProgress.value)) / 100) *
        circumference,
  }));
  const publishedArcProps = useAnimatedProps(() => ({
    strokeDashoffset:
      circumference -
      (Math.max(0, Math.min(100, publishedProgress.value)) / 100) *
        circumference,
  }));

  const center = size / 2;
  const transform = `rotate(-90 ${center} ${center})`;

  return (
    <View style={[styles.ring, { height: size, width: size }]}>
      <Svg height={size} width={size}>
        <Circle
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={theme.sk.color.border.primary}
          strokeWidth={strokeWidth}
        />
        <AnimatedCircle
          animatedProps={totalArcProps}
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={theme.sk.color.icon.negative.secondary}
          strokeDasharray={circumference}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          transform={transform}
        />
        <AnimatedCircle
          animatedProps={overriddenArcProps}
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={theme.sk.color.icon.blue}
          strokeDasharray={circumference}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          transform={transform}
        />
        <AnimatedCircle
          animatedProps={acceptedArcProps}
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={theme.sk.color.icon.positive.secondary}
          strokeDasharray={circumference}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          transform={transform}
        />
        <AnimatedCircle
          animatedProps={publishedArcProps}
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={theme.sk.color.icon.primary}
          strokeDasharray={circumference}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          transform={transform}
        />
      </Svg>
      <View style={styles.ringLabel}>
        <Typography variant={labelVariant}>
          {`${Math.round(progressPercent)}%`}
        </Typography>
      </View>
    </View>
  );
};

const useStyles = createStyles(() => ({
  ring: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  ringLabel: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
}));
